// Shared by the React app (<Seo>) and the build step (scripts/prerender.mjs),
// so the tags baked into static HTML are exactly the tags the app renders.
// Keep this file free of browser and React APIs: it also runs in Node.

// Canonical origin. rhinorecon.com 302s to www, so every canonical/og:url must
// point at www or search engines see two copies of each page.
export const SITE_URL = "https://www.rhinorecon.com";
export const SITE_NAME = "Rhino Recon";

export const DEFAULT_DESCRIPTION =
  "Rhino Recon gives contractors, roofers, and sales teams property data and verified owner contact info (phone numbers and emails) for 59M+ properties across 27 US states.";

// The share image has the state count baked in. Bump ?v= whenever the image
// changes: Facebook, LinkedIn and Slack cache previews by image URL.
export const DEFAULT_IMAGE = `${SITE_URL}/images/og-default.png?v=27-states`;

// Hand-written meta for the code-driven marketing pages. The CMS meta for these
// routes is placeholder text ("Pricing", "Rhino Ricon Support Resources").
export const PAGE_SEO = {
  home: {
    title: "Rhino Recon | Property Owner Contact Info & Property Data",
    description: DEFAULT_DESCRIPTION,
  },
  about: {
    title: "About Rhino Recon | Property Data for Door-to-Door Sales Teams",
    description:
      "Rhino Recon was built by contractors for contractors: find properties, see who owns them, and reach the decision maker directly, without knocking blind.",
  },
  pricing: {
    title: "Pricing | Rhino Recon Plans for Property Owner Data",
    description:
      "Compare Rhino Recon plans. Monthly and annual subscriptions with property search, owner phone numbers and emails, weather (hail and wind) data, and exports.",
  },
};

export const industryTitle = (industryName) =>
  `${industryName} Leads: Property Owner Contact Info | ${SITE_NAME}`;

// ------------------------------------------------------------------ text ---

const NAMED_ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  rsquo: "’", lsquo: "‘", rdquo: "”", ldquo: "“",
  ndash: "–", mdash: "—", hellip: "…", bull: "•",
  copy: "©", reg: "®", trade: "™",
};

const decodeEntities = (text) =>
  text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, code) => {
    if (code[0] === "#") {
      const point = code[1].toLowerCase() === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(point) ? String.fromCodePoint(point) : entity;
    }
    return NAMED_ENTITIES[code.toLowerCase()] ?? entity;
  });

// CMS fields are HTML. Tags become spaces (so "<p>a</p><p>b</p>" does not turn
// into "ab") and entities are decoded, or &#39; would ship double-escaped.
export const stripHtml = (html) =>
  decodeEntities(String(html ?? "").replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();

// First ~160 characters of body copy, cut on a word boundary.
export const excerpt = (html, max = 160) => {
  const text = stripHtml(html);
  if (text.length <= max) return text;
  return `${text.slice(0, text.lastIndexOf(" ", max))}…`;
};

// Most CMS descriptions are placeholders ("Pest Control", "Sign up"). Anything
// this short is not a real description, so use the fallback instead.
const MIN_DESCRIPTION_LENGTH = 70;

export const pickDescription = (cmsDescription, fallback) => {
  const cms = String(cmsDescription ?? "").trim();
  return cms.length >= MIN_DESCRIPTION_LENGTH ? cms : fallback;
};

// ------------------------------------------------------------- JSON-LD ---

export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  sameAs: ["https://www.youtube.com/watch?v=-1job-XWJAM"],
};

export const WEBSITE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
};

export const SOFTWARE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE_NAME,
  url: SITE_URL,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: DEFAULT_DESCRIPTION,
  offers: { "@type": "Offer", url: `${SITE_URL}/pricing` },
};

// Returns null when there are no answered questions: an empty FAQPage is
// invalid structured data. seoTags() drops null entries.
export const faqJsonLd = (items) => {
  const questions = items
    .filter((item) => item?.question && item?.answer)
    .map((item) => ({
      "@type": "Question",
      name: stripHtml(item.question),
      acceptedAnswer: { "@type": "Answer", text: stripHtml(item.answer) },
    }));
  if (questions.length === 0) return null;
  return { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: questions };
};

// The CMS stores FAQs as { rows: [{ title, content }] } (pricing, support).
export const cmsFaqJsonLd = (faqs) =>
  faqJsonLd((faqs?.rows ?? []).map((row) => ({ question: row.title, answer: row.content })));

export const articleJsonLd = ({ title, description, image, url, published, modified, authorName }) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  description,
  image: image || DEFAULT_IMAGE,
  url,
  mainEntityOfPage: url,
  datePublished: published,
  dateModified: modified || published,
  author: authorName
    ? { "@type": "Person", name: authorName }
    : { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  publisher: { "@type": "Organization", name: SITE_NAME, logo: ORGANIZATION_JSON_LD.logo },
});

// ------------------------------------------------------- page-type SEO ---
// Props for <Seo>, per kind of CMS page. The build step calls the same
// functions with the same API responses.

export const cmsPageSeo = ({ pageTitle, meta, fallbackDescription }) => ({
  title: pageTitle,
  description: pickDescription(meta?.meta_description, fallbackDescription),
  keywords: meta?.meta_keywords,
  image: meta?.meta_thumb,
});

export const industrySeo = ({ industry, meta }) =>
  cmsPageSeo({
    pageTitle: industryTitle(industry.title),
    meta,
    fallbackDescription: excerpt(industry.section1_text),
  });

// The CMS "meta_description" of a post is a keyword list, so the description
// is the opening of the post itself.
export const blogPostSeo = ({ encodedId, blog, meta, pageTitle, uploadsUrl }) => {
  const image = blog.image ? `${uploadsUrl}blog/${blog.image}` : meta?.meta_thumb;
  const description = excerpt(blog.detail);
  const url = `${SITE_URL}/blog-detail/${encodedId}/${blog.slug}`;
  return {
    title: pageTitle,
    description,
    keywords: meta?.meta_keywords,
    image,
    type: "article",
    jsonLd: [
      articleJsonLd({
        title: blog.title,
        description,
        image,
        url,
        published: blog.created_at,
        modified: blog.updated_at,
        authorName: blog.writter_name,
      }),
    ],
  };
};

// ---------------------------------------------------------------- tags ---

// Canonical is always the www origin + pathname, never the query string:
// ?utm_source=... variants must collapse onto one URL.
export const canonicalUrl = (pathname) =>
  `${SITE_URL}${pathname === "/" ? "/" : pathname.replace(/\/+$/, "")}`;

/**
 * Every head tag for one page, as plain data. <Seo> turns it into React
 * elements; the build step turns it into HTML strings.
 */
export const seoTags = ({
  pathname,
  title,
  description,
  image,
  keywords,
  type = "website",
  noindex = false,
  jsonLd = [],
}) => {
  const url = canonicalUrl(pathname);
  const pageTitle = title || SITE_NAME;
  const pageDescription = description || DEFAULT_DESCRIPTION;
  const pageImage = image || DEFAULT_IMAGE;

  return {
    title: pageTitle,
    meta: [
      { name: "description", content: pageDescription },
      keywords && { name: "keywords", content: keywords },
      { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:type", content: type },
      { property: "og:url", content: url },
      { property: "og:title", content: pageTitle },
      { property: "og:description", content: pageDescription },
      { property: "og:image", content: pageImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: pageTitle },
      { name: "twitter:description", content: pageDescription },
      { name: "twitter:image", content: pageImage },
    ].filter(Boolean),
    links: noindex ? [] : [{ rel: "canonical", href: url }],
    jsonLd: noindex ? [] : jsonLd.filter(Boolean),
  };
};
